// ThinkViews — the other pictures of one think stream. BrainThink.js owns
// the stream (events, cursor, fold) and the transport; these components draw
// it. Identical in the plane and the kit (scope: both). Every view obeys the
// one rule of the player: draw only what was emitted — the curve has a point
// per event, the race re-sorts on `select`, the waterfall's bars end where
// `leg.hits` landed, the tape is `describeEvent` per line. Nothing is
// interpolated, nothing is invented.
//
//   Scorecard   the verdict frame — searched → kept → cited, legs, time, a stamp
//   EvidenceCurve   distinct sources over time, kept / used / cited as drops
//   BarRace     candidates as bars re-sorting as fusion and rerank land
//   Waterfall   devtools-style timing: a bar per leg, instants, the answer
//   Tape        the stream as a typewriter log
//   Sonar       the constellation with time as the angle and a sweeping hand
//   Ribbons     legs → sources → outcome, as a sankey
//   HeatGrid    the whole corpus as cells; served-lately as heat, this search lit
//   Trails      the last N searches as footprints: which pages keep being served
//   Compare     two streams of one question side by side, with a verdict
//
// [[watch-the-brain-think]]
import React, { useEffect, useMemo, useRef } from 'react';
import { foldThink, describeEvent } from './BrainThink.js';
import './ThinkViews.css';

const LEG_LABEL = { vector: 'meaning', keyword: 'words', graph: 'related', brain: 'brain' };
const PHASE_RANK = { candidate: 1, selected: 2, opened: 3, cited: 4 };
const PHASE_R = { candidate: 0.92, selected: 0.6, cited: 0.34, opened: 0.14 };
const STRATUM_LABEL = { 'app-brain': "this app's brain", brain: 'project brain', docs: 'product docs', design: 'app design', 'kit-docs': 'kit docs', org: 'documents' };
const clip = (s, n = 34) => (String(s ?? '').length > n ? `${String(s).slice(0, n - 1)}…` : String(s ?? ''));
const fmtMs = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);
const upto = (events, cursor) => events.slice(0, Math.min(cursor, events.length - 1) + 1);
const spanOf = (events) => Math.max(1, ...events.map((e) => e.at || 0));

// The doc ids an event names, in order.
function docsOf(ev) {
  if (!ev) return [];
  if (ev.type === 'leg.hits' || ev.type === 'brain.search' || ev.type === 'select') return (ev.hits || []).map((h) => h.docId);
  if (ev.type === 'context') return ev.docIds || [];
  if (ev.type === 'brain.open') return ev.docId ? [ev.docId] : [];
  if (ev.type === 'answer.done') return ev.citations || [];
  return [];
}
// docId → the `at` it was first named.
function firstSeen(events, cursor) {
  const m = new Map();
  for (const ev of upto(events, cursor)) for (const d of docsOf(ev)) if (d && !m.has(d)) m.set(d, ev.at || 0);
  return m;
}
const lastOf = (events, cursor, type) => { const list = upto(events, cursor); for (let i = list.length - 1; i >= 0; i -= 1) if (list[i].type === type) return list[i]; return null; };

// ---- the scorecard --------------------------------------------------------------
// Counts from the fold, a verdict from the stream: searching while live,
// grounded when every citation was a kept source, nothing found / declined
// from answer.done, "walked" for a brain walk that never asked.
export function verdictOf(fold, { live = false, events = [], cursor = -1 } = {}) {
  const done = lastOf(events, cursor, 'answer.done');
  const start = lastOf(events, cursor, 'ask.start');
  const withheld = start?.withheld?.length || 0;
  if (done?.declined) return { id: 'declined', label: 'declined', tone: 'warn', note: 'a policy refused before searching' };
  if (done?.empty) return { id: 'empty', label: 'nothing found', tone: 'muted', note: withheld ? `with ${withheld} page${withheld === 1 ? '' : 's'} withheld, nothing relevant remained` : 'no relevant source' };
  if (done) {
    const n = done.citations?.length || 0;
    if (!n) return { id: 'uncited', label: 'answered uncited', tone: 'warn', note: 'the answer cited nothing' };
    // Grounded = every citation was a source the search kept (select used /
    // context) before the answer was written — the out-of-sample check.
    const handed = new Set();
    for (const ev of upto(events, cursor)) { if (ev.type === 'select') for (const h of ev.hits || []) if (h.used) handed.add(h.docId); if (ev.type === 'context') for (const d of ev.docIds || []) handed.add(d); if (ev.type === 'brain.open' && ev.docId) handed.add(ev.docId); }
    const stray = handed.size ? (done.citations || []).filter((d) => !handed.has(d)) : [];
    if (stray.length) return { id: 'stray', label: 'stray citation', tone: 'warn', note: `${stray.length} of ${n} citation${n === 1 ? '' : 's'} not among the sources handed to the model` };
    return { id: 'grounded', label: 'grounded', tone: 'ok', note: `${n} citation${n === 1 ? '' : 's'}, every one a kept source${withheld ? ` · ${withheld} withheld` : ''}` };
  }
  if (live) return { id: 'searching', label: 'searching', tone: 'info', note: 'the stream is still arriving' };
  if (fold.counts['brain.open'] || fold.counts['brain.search']) return { id: 'walked', label: 'walked the brain', tone: 'muted', note: 'pages searched and opened, no answer asked' };
  if (!fold.docs.length && (fold.counts.guide || fold.counts.lookup || fold.counts.tool)) return { id: 'acted', label: 'from the console', tone: 'muted', note: 'the guide, your records or a tool — no search ran' };
  return { id: 'searched', label: 'searched', tone: 'muted', note: 'no answer recorded on this stream' };
}
export function scoreOf(fold, { live = false, events = [], cursor = -1 } = {}) {
  const legs = new Set();
  for (const d of fold.docs) for (const l of d.legs) legs.add(l);
  if (fold.counts['brain.search']) legs.add('brain');
  const done = lastOf(events, cursor, 'answer.done');
  const last = upto(events, cursor).slice(-1)[0];
  return {
    searched: fold.docs.length,
    kept: fold.docs.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).length,
    opened: fold.docs.filter((d) => d.phase === 'opened').length,
    cited: fold.docs.filter((d) => d.phase === 'cited').length,
    legs: [...legs].map((l) => LEG_LABEL[l] || l),
    variants: lastOf(events, cursor, 'query.rewrite')?.variants?.length || 1,
    ms: done?.ms || last?.at || 0,
    verdict: verdictOf(fold, { live, events, cursor }),
  };
}
export function Scorecard({ fold, events = [], cursor = -1, live = false, compact = false }) {
  const s = useMemo(() => scoreOf(fold, { live, events, cursor }), [fold, live, events, cursor]);
  if (compact) {
    return (
      <span className={`tv-stamp is-${s.verdict.tone} ${live && s.verdict.id === 'searching' ? 'is-live' : ''}`} title={s.verdict.note}>
        <b>{s.verdict.label}</b>
        <span>{s.searched} → {s.kept}{s.opened ? ` → ${s.opened}` : ''} → {s.cited}</span>
      </span>
    );
  }
  return (
    <div className={`tv-card is-${s.verdict.tone}`} role="group" aria-label="Scorecard">
      <div className="tv-card-nums">
        <div><b>{s.searched}</b><span>searched</span></div>
        <i>→</i>
        <div><b>{s.kept}</b><span>kept</span></div>
        {s.opened ? <><i>→</i><div><b>{s.opened}</b><span>opened</span></div></> : null}
        <i>→</i>
        <div><b>{s.cited}</b><span>cited</span></div>
      </div>
      <div className={`tv-card-stamp ${live && s.verdict.id === 'searching' ? 'is-live' : ''}`}><b>{s.verdict.label}</b><small>{s.verdict.note}</small></div>
      <dl className="tv-card-meta">
        <dt>legs</dt><dd>{s.legs.length ? s.legs.join(' · ') : '—'}</dd>
        <dt>phrasings</dt><dd>{s.variants}</dd>
        <dt>time</dt><dd>{s.ms ? fmtMs(s.ms) : '—'}</dd>
      </dl>
    </div>
  );
}

// ---- the evidence curve ----------------------------------------------------------
// One point per event: distinct sources named so far. Kept (fuse), used
// (select), handed to the model (context) and cited (answer.done) draw as
// drops below the line — the funnel over time. Where select events carry
// scores, the best score draws as a thinner line on its own scale.
export function curveOf(events, cursor) {
  const seen = new Set();
  const pts = []; const marks = []; const scores = [];
  for (const ev of upto(events, cursor)) {
    for (const d of docsOf(ev)) if (d) seen.add(d);
    pts.push({ at: ev.at || 0, n: seen.size, type: ev.type });
    if (ev.type === 'fuse') marks.push({ at: ev.at || 0, n: ev.kept, label: 'kept' });
    if (ev.type === 'select') { const used = (ev.hits || []).filter((h) => h.used).length; marks.push({ at: ev.at || 0, n: used, label: ev.after === 'rerank' ? 'reranked' : 'selected' }); const best = Math.max(0, ...(ev.hits || []).map((h) => Number(h.score) || 0)); if (best) scores.push({ at: ev.at || 0, v: best }); }
    if (ev.type === 'context') marks.push({ at: ev.at || 0, n: (ev.docIds || []).length || ev.blocks, label: 'to the model' });
    if (ev.type === 'answer.done') marks.push({ at: ev.at || 0, n: (ev.citations || []).length, label: ev.empty ? 'nothing' : ev.declined ? 'declined' : 'cited' });
  }
  return { pts, marks, scores };
}
export function EvidenceCurve({ events, cursor, width = 600, height = 260, live = false }) {
  const span = spanOf(events);
  const { pts, marks, scores } = useMemo(() => curveOf(events, cursor), [events, cursor]);
  const pad = { l: 34, r: 16, t: 14, b: 26 };
  const W = width; const H = height;
  const maxN = Math.max(1, ...pts.map((p) => p.n));
  const x = (at) => pad.l + ((at || 0) / span) * (W - pad.l - pad.r);
  const y = (n) => H - pad.b - (n / maxN) * (H - pad.t - pad.b);
  const maxS = Math.max(0.0001, ...scores.map((s) => s.v));
  const ys = (v) => H - pad.b - (v / maxS) * (H - pad.t - pad.b) * 0.9;
  let d = '';
  pts.forEach((p, i) => { const px = x(p.at); const py = y(p.n); d += i ? ` H ${px} V ${py}` : `M ${px} ${py}`; });
  const last = pts[pts.length - 1];
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxN * f)).filter((v, i, a) => a.indexOf(v) === i);
  return (
    <svg className="tv-curve" viewBox={`0 0 ${W} ${H}`} style={{ height }} role="img" aria-label={`Evidence curve: ${last ? `${last.n} sources named` : 'empty'}`}>
      {ticks.map((v) => <g key={v} className="tv-grid"><line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} /><text x={pad.l - 6} y={y(v) + 3} textAnchor="end">{v}</text></g>)}
      <g className="tv-axis"><line x1={pad.l} x2={W - pad.r} y1={H - pad.b} y2={H - pad.b} /><text x={pad.l} y={H - 8}>0</text><text x={W - pad.r} y={H - 8} textAnchor="end">{fmtMs(span)}</text></g>
      {d ? <path className="tv-curve-area" d={`${d} V ${H - pad.b} H ${pad.l} Z`} /> : null}
      {d ? <path className="tv-curve-line" d={d} /> : null}
      {scores.length > 1 ? <path className="tv-curve-score" d={scores.map((s, i) => `${i ? 'L' : 'M'} ${x(s.at)} ${ys(s.v)}`).join(' ')} /> : null}
      {scores.map((s, i) => <circle key={`s${i}`} className="tv-curve-score-dot" cx={x(s.at)} cy={ys(s.v)} r={3}><title>{`best score ${s.v}`}</title></circle>)}
      {marks.map((m, i) => (
        <g key={i} className={`tv-curve-mark is-${m.label.replace(/\s+/g, '-')}`} style={{ transform: `translate(${x(m.at)}px, 0)` }}>
          <line y1={y(m.n)} y2={H - pad.b} />
          <circle cy={y(m.n)} r={4} />
          <text y={y(m.n) - 8} textAnchor="middle">{m.n} {m.label}</text>
        </g>
      ))}
      {last ? <circle className={`tv-curve-head ${live ? 'is-live' : ''}`} cx={x(last.at)} cy={y(last.n)} r={4} /> : null}
      {last ? <text className="tv-curve-n" x={Math.min(x(last.at) + 8, W - pad.r - 30)} y={y(last.n) + 4}>{last.n}</text> : null}
    </svg>
  );
}

// ---- the bar-chart race ----------------------------------------------------------
// Rows are the candidates; their order is the newest `select` (fused score,
// then the reranked order) and, before any select, the legs that found them.
// Rows keep their key, so a change of order is a slide, not a redraw.
export function rankingAt(events, cursor, fold) {
  const sel = lastOf(events, cursor, 'select');
  if (sel?.hits?.length) {
    const max = Math.max(0.0001, ...sel.hits.map((h) => Number(h.score) || 0));
    return sel.hits.map((h, i) => ({ docId: h.docId, title: h.title || h.docId, v: Number(h.score) ? Number(h.score) / max : 1 - i / sel.hits.length, used: !!h.used, note: Number(h.score) ? Number(h.score).toFixed(3) : `#${i + 1}` }));
  }
  const first = firstSeen(events, cursor);
  return fold.docs.slice().sort((a, b) => b.legs.size - a.legs.size || (first.get(a.docId) || 0) - (first.get(b.docId) || 0)).map((d, i, a) => ({ docId: d.docId, title: d.title, v: d.legs.size / Math.max(1, ...a.map((x) => x.legs.size)), used: false, note: [...d.legs].map((l) => LEG_LABEL[l] || l).join(' + ') || '—' }));
}
export function BarRace({ events, cursor, fold, rows = 12, labels = true, hrefFor = null, LinkComponent = 'a' }) {
  const Link = LinkComponent;
  const ranking = useMemo(() => rankingAt(events, cursor, fold), [events, cursor, fold]);
  const phase = useMemo(() => new Map(fold.docs.map((d) => [d.docId, d.phase])), [fold.docs]);
  const list = ranking.slice(0, rows);
  const rowH = 26;
  const sel = lastOf(events, cursor, 'select');
  return (
    <div className="tv-race" style={{ height: Math.max(3, list.length) * rowH + 22 }} role="list" aria-label="Candidates by score">
      <div className="tv-race-head">{sel ? (sel.after === 'rerank' ? 'reranked order' : 'fused score (weighted reciprocal rank)') : fold.docs.length ? 'found so far — by how many legs agree' : 'waiting for the first leg'}</div>
      {list.map((r, i) => {
        const p = phase.get(r.docId) || 'candidate';
        const href = labels && hrefFor ? hrefFor(r.docId) : null;
        const label = labels ? clip(r.title, 48) : `source ${String(r.docId).slice(-6)}`;
        return (
          <div key={r.docId} role="listitem" className={`tv-race-row is-${p} ${r.used ? 'is-used' : ''} ${fold.touched.has(r.docId) ? 'is-hot' : ''}`} style={{ transform: `translateY(${22 + i * rowH}px)` }}>
            <span className="tv-race-rank">{i + 1}</span>
            <span className="tv-race-bar" style={{ width: `${Math.max(2, r.v * 100)}%` }} />
            <span className="tv-race-label">{href ? <Link to={href} href={href}>{label}</Link> : label}</span>
            <span className="tv-race-note">{r.note}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---- the waterfall -----------------------------------------------------------------
// A bar per leg from `leg.start` to its `leg.hits` (open-ended while live),
// an instant per fuse / siblings / rerank / select / context / brain step,
// and the answer from `answer.start` to `answer.done`.
export function spansOf(events, cursor) {
  const list = upto(events, cursor);
  const now = list.length ? list[list.length - 1].at || 0 : 0;
  const rows = [];
  const open = new Map();
  for (const ev of list) {
    const at = ev.at || 0;
    if (ev.type === 'leg.start') { const k = `${ev.leg}:${ev.variant || 0}`; const row = { key: `leg-${k}-${ev.seq}`, label: `${LEG_LABEL[ev.leg] || ev.leg}${ev.variant ? ` · v${ev.variant + 1}` : ''}`, kind: 'leg', from: at, to: null, note: ev.engine || (ev.words ? ev.words.slice(0, 4).join(' ') : ev.seeds ? `${ev.seeds.length} seeds` : '') }; rows.push(row); open.set(k, row); } else if (ev.type === 'leg.hits') { const k = `${ev.leg}:${ev.variant || 0}`; const row = open.get(k); if (row && row.to == null) { row.to = at; row.note = `${ev.total ?? ev.hits?.length ?? 0} hits`; open.delete(k); } else rows.push({ key: `hits-${ev.seq}`, label: `${LEG_LABEL[ev.leg] || ev.leg} hits`, kind: 'leg', from: at, to: at, note: `${ev.total ?? 0}` }); } else if (ev.type === 'answer.start') { const row = { key: `answer-${ev.seq}`, label: 'answer', kind: 'answer', from: at, to: null, note: 'writing' }; rows.push(row); open.set('answer', row); } else if (ev.type === 'answer.done') { const row = open.get('answer'); if (row) { row.to = at; row.note = ev.declined ? 'declined' : ev.empty ? 'nothing found' : `${ev.citations?.length || 0} cited`; open.delete('answer'); } else rows.push({ key: `done-${ev.seq}`, label: 'answer', kind: 'answer', from: at, to: at, note: ev.empty ? 'nothing found' : `${ev.citations?.length || 0} cited` }); } else if (ev.type !== 'ask.start') { rows.push({ key: `${ev.type}-${ev.seq}`, label: ev.type === 'brain.open' ? `open ${clip(ev.title || ev.slug, 22)}` : ev.type === 'brain.search' ? 'brain search' : ev.type === 'tool' ? `tool ${ev.name}` : ev.type === 'guide' ? 'console guide' : ev.type === 'lookup' ? `lookup ${ev.entity || ''}` : ev.type.replace('query.', ''), kind: ev.type.startsWith('brain') || ['tool', 'guide', 'lookup'].includes(ev.type) ? 'brain' : 'step', from: at, to: at, note: ev.type === 'fuse' ? `${ev.candidates} → ${ev.kept}` : ev.type === 'siblings' ? `${ev.demoted} demoted` : ev.type === 'rerank' ? `top ${ev.k}` : ev.type === 'select' ? `${(ev.hits || []).filter((h) => h.used).length} used` : ev.type === 'context' ? `${ev.blocks} blocks` : ev.type === 'query.rewrite' ? `${ev.variants?.length || 0} phrasings` : ev.type === 'brain.search' ? `${ev.slugs?.length || ev.hits?.length || 0} pages` : '' }); }
  }
  for (const row of open.values()) { row.to = now; row.running = true; } // still running at the cursor
  return rows;
}
export function Waterfall({ events, cursor, width = 600, live = false }) {
  const span = spanOf(events);
  const rows = useMemo(() => spansOf(events, cursor), [events, cursor]);
  const rowH = 22; const labelW = 150; const noteW = 90;
  const W = width; const H = rows.length * rowH + 28;
  const x = (at) => labelW + ((at || 0) / span) * (W - labelW - noteW - 8);
  const cur = upto(events, cursor).slice(-1)[0]?.at || 0;
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  return (
    <svg className="tv-wf" viewBox={`0 0 ${W} ${H}`} style={{ height: H }} role="img" aria-label={`Timing: ${rows.length} steps over ${fmtMs(span)}`}>
      {ticks.map((f) => <g key={f} className="tv-grid"><line x1={x(span * f)} x2={x(span * f)} y1={18} y2={H - 6} /><text x={x(span * f)} y={12} textAnchor={f === 1 ? 'end' : f === 0 ? 'start' : 'middle'}>{fmtMs(span * f)}</text></g>)}
      {rows.map((r, i) => {
        const y = 22 + i * rowH;
        const open = !!r.running && live;
        const instant = r.from === r.to && !r.running;
        const x1 = x(r.from); const x2 = Math.max(x1 + 2, x(r.to));
        return (
          <g key={r.key} className={`tv-wf-row is-${r.kind} ${open ? 'is-open' : ''}`} style={{ transform: `translate(0, ${y}px)` }}>
            <text x={labelW - 8} y={rowH / 2 + 4} textAnchor="end" className="tv-wf-label">{clip(r.label, 24)}</text>
            {instant ? <circle cx={x1} cy={rowH / 2} r={4} className="tv-wf-dot" /> : <rect x={x1} y={5} width={x2 - x1} height={rowH - 10} rx={3} className="tv-wf-bar" />}
            <text x={W - noteW + 4} y={rowH / 2 + 4} className="tv-wf-note">{instant || r.running ? '' : `${fmtMs(r.to - r.from)} · `}{clip(r.note, 18)}</text>
          </g>
        );
      })}
      <line className="tv-wf-cursor" x1={x(cur)} x2={x(cur)} y1={18} y2={H - 6} />
    </svg>
  );
}

// ---- the tape --------------------------------------------------------------------------
export function Tape({ events, cursor, live = false, labels = true, noMotion = false }) {
  const ref = useRef(null);
  const list = upto(events, cursor);
  useEffect(() => { const el = ref.current; if (el) el.scrollTop = el.scrollHeight; }, [cursor]);
  return (
    <ol ref={ref} className={`tv-tape ${noMotion ? 'no-motion' : ''}`} aria-label="Think log">
      {list.map((ev, i) => (
        <li key={ev.seq ?? i} className={`tv-tape-line is-${ev.type.replace('.', '-')} ${i === list.length - 1 ? 'is-new' : ''}`}>
          <span className="tv-tape-at">{((ev.at || 0) / 1000).toFixed(2)}s</span>
          <span className="tv-tape-type">{ev.type}</span>
          <span className="tv-tape-text">{labels ? describeEvent(ev) : describeEvent({ ...ev, question: undefined, variants: undefined, words: undefined, title: undefined, slug: undefined, query: undefined, name: undefined })}</span>
        </li>
      ))}
      {live ? <li className="tv-tape-line is-cursor"><span className="tv-tape-at" /><span className="tv-tape-type" /><span className="tv-tape-text"><i className="tv-caret" /></span></li> : null}
    </ol>
  );
}

// ---- the sonar --------------------------------------------------------------------------
// The constellation with time as the angle: a source sits where the hand
// was when it was first named (the whole stream is one revolution), at the
// radius of its phase. The hand is at the current event; what it has passed
// is lit, what it has not is not yet there. Live, the revolution grows.
export function Sonar({ fold, events, cursor, width = 600, height = 320, labels = true, live = false, noMotion = false, hrefFor = null, LinkComponent = 'a' }) {
  const Link = LinkComponent;
  const W = width; const H = Math.max(160, height);
  const cx = W / 2; const cy = H / 2; const R = Math.min(W, H) / 2 - 16;
  const span = spanOf(events);
  const first = useMemo(() => firstSeen(events, cursor), [events, cursor]);
  const cur = upto(events, cursor).slice(-1)[0]?.at || 0;
  const angOf = (at) => -Math.PI / 2 + ((at || 0) / span) * Math.PI * 2 * 0.98;
  const hand = angOf(cur);
  const nodes = fold.docs.map((d) => { const a = angOf(first.get(d.docId) || 0); const r = R * (PHASE_R[d.phase] || PHASE_R.candidate); return { ...d, ang: a, x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r }; });
  const wedge = (() => { const a1 = hand - 0.9; const p = (a) => `${cx + Math.cos(a) * R} ${cy + Math.sin(a) * R}`; return `M ${cx} ${cy} L ${p(a1)} A ${R} ${R} 0 0 1 ${p(hand)} Z`; })();
  const done = !!fold.counts['answer.done'];
  return (
    <svg className={`tv-sonar ${noMotion ? 'no-motion' : ''}`} viewBox={`0 0 ${W} ${H}`} style={{ height }} role="img" aria-label={`Sonar: ${fold.docs.length} sources over ${fmtMs(span)}`}>
      <defs><radialGradient id="tv-sonar-g"><stop offset="0%" stopColor="var(--ok)" stopOpacity="0" /><stop offset="100%" stopColor="var(--ok)" stopOpacity="0.25" /></radialGradient></defs>
      {['candidate', 'selected', 'cited'].map((p) => <circle key={p} cx={cx} cy={cy} r={R * PHASE_R[p]} className={`tv-sonar-ring is-${p}`} />)}
      <line x1={cx - R} x2={cx + R} y1={cy} y2={cy} className="tv-sonar-cross" /><line x1={cx} x2={cx} y1={cy - R} y2={cy + R} className="tv-sonar-cross" />
      {!done || live ? <path d={wedge} className="tv-sonar-wedge" fill="url(#tv-sonar-g)" /> : null}
      <line x1={cx} y1={cy} x2={cx + Math.cos(hand) * R} y2={cy + Math.sin(hand) * R} className={`tv-sonar-hand ${live && !done ? 'is-live' : ''}`} />
      {nodes.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).map((d) => <line key={`l-${d.docId}`} x1={cx} y1={cy} x2={d.x} y2={d.y} className={`tv-sonar-line is-${d.phase}`} />)}
      <circle cx={cx} cy={cy} r={5} className="tv-sonar-q" />
      {nodes.map((d) => {
        const hot = !noMotion && fold.touched.has(d.docId);
        const href = labels && hrefFor ? hrefFor(d.docId) : null;
        const show = labels && PHASE_RANK[d.phase] >= PHASE_RANK.selected;
        const right = Math.cos(d.ang) >= 0;
        const dot = (
          <g key={d.docId} className={`tv-sonar-node is-${d.phase} ${hot ? 'is-hot' : ''}`} style={{ transform: `translate(${d.x}px, ${d.y}px)` }}>
            {hot ? <circle r={13} className="tv-sonar-pulse" /> : null}
            <circle r={d.phase === 'candidate' ? 3.5 : 5.5} />
            {show ? <text x={right ? 9 : -9} y={3} textAnchor={right ? 'start' : 'end'} className="tv-sonar-label">{clip(d.title)}</text> : null}
          </g>
        );
        return href ? <Link key={d.docId} to={href} href={href} className="tv-sonar-link">{dot}</Link> : dot;
      })}
      <text x={cx} y={16} textAnchor="middle" className="tv-sonar-t">0</text>
      <text x={cx + R + 4} y={cy + 4} className="tv-sonar-t">{fmtMs(span / 4)}</text>
      <text x={cx} y={cy + R + 14} textAnchor="middle" className="tv-sonar-t">{fmtMs(span / 2)}</text>
      <text x={cx - R - 4} y={cy + 4} textAnchor="end" className="tv-sonar-t">{fmtMs((span * 3) / 4)}</text>
    </svg>
  );
}

// ---- the ribbons -----------------------------------------------------------------------
// legs → sources → outcome. Candidates that were never kept gather into one
// node so the picture stays the answer's, not the corpus's.
export function Ribbons({ fold, width = 600, height = 320, labels = true, hrefFor = null, LinkComponent = 'a' }) {
  const Link = LinkComponent;
  const W = width; const H = Math.max(180, height);
  // Weights: a kept source is 1, split evenly across the legs that found
  // it; the not-kept bundle weighs at most 3. A leg's weight is the sum of
  // what flows through it, an outcome's the sum of what lands — so every
  // ribbon is the same height at both ends.
  const model = useMemo(() => {
    const kept = fold.docs.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).sort((a, b) => PHASE_RANK[b.phase] - PHASE_RANK[a.phase]);
    const dropped = fold.docs.filter((d) => PHASE_RANK[d.phase] < PHASE_RANK.selected);
    const legsOf = (d) => (d.legs.size ? [...d.legs] : fold.counts['brain.search'] ? ['brain'] : []);
    const mid = kept.map((d) => ({ id: d.docId, label: d.title, phase: d.phase, n: 1, legs: legsOf(d), out: d.phase === 'cited' ? 'cited' : d.phase === 'opened' ? 'opened' : 'kept' }));
    if (dropped.length) mid.push({ id: '__dropped', label: `${dropped.length} candidate${dropped.length === 1 ? '' : 's'} not kept`, phase: 'candidate', n: Math.min(3, dropped.length), legs: [...new Set(dropped.flatMap(legsOf))], out: 'dropped' });
    const flows = []; // { leg, mid, w }
    for (const m of mid) for (const l of m.legs) flows.push({ leg: l, mid: m.id, w: m.n / m.legs.length, phase: m.phase });
    const legs = ['vector', 'keyword', 'graph', 'brain'].map((l) => ({ id: l, label: LEG_LABEL[l], n: flows.filter((f) => f.leg === l).reduce((a, f) => a + f.w, 0), count: fold.docs.filter((d) => legsOf(d).includes(l)).length })).filter((l) => l.n > 0);
    const out = [['cited', 'cited'], ['opened', 'opened'], ['kept', 'kept, not cited'], ['dropped', 'not kept']].map(([id, label]) => ({ id, label, n: mid.filter((m) => m.out === id).reduce((a, m) => a + m.n, 0), count: mid.filter((m) => m.out === id).length })).filter((o) => o.n > 0);
    return { legs, mid, out, flows, dropped };
  }, [fold]);
  const total = model.mid.reduce((a, m) => a + m.n, 0) || 1;
  const col = (items, x, w) => {
    const gap = 6; const avail = H - 24 - gap * Math.max(0, items.length - 1);
    let y = 12;
    return items.map((it) => { const h = Math.max(4, (it.n / total) * avail); const r = { ...it, x, w, y, h }; y += h + gap; return r; });
  };
  const L = col(model.legs, 8, 14); const M = col(model.mid, W / 2 - 7, 14); const O = col(model.out, W - 22, 14);
  const unit = (H - 24 - 6 * Math.max(0, model.mid.length - 1)) / total; // px per weight
  const ribbon = (a, b, ay, by, h) => { const x1 = a.x + a.w; const x2 = b.x; const mx = (x1 + x2) / 2; return `M ${x1} ${ay} C ${mx} ${ay} ${mx} ${by} ${x2} ${by} v ${h} C ${mx} ${by + h} ${mx} ${ay + h} ${x1} ${ay + h} Z`; };
  const links = [];
  const lOff = new Map(L.map((l) => [l.id, l.y])); const mOff = new Map(M.map((m) => [m.id, m.y]));
  for (const f of model.flows) {
    const l = L.find((x) => x.id === f.leg); const m = M.find((x) => x.id === f.mid);
    if (!l || !m) continue;
    const h = Math.max(1.5, f.w * unit);
    links.push({ key: `${f.leg}-${f.mid}`, d: ribbon(l, m, lOff.get(l.id), mOff.get(m.id), h), phase: f.phase });
    lOff.set(l.id, lOff.get(l.id) + h); mOff.set(m.id, mOff.get(m.id) + h);
  }
  const oOff = new Map(O.map((o) => [o.id, o.y]));
  for (const m of M) {
    const o = O.find((x) => x.id === m.out);
    if (!o) continue;
    const h = Math.max(1.5, m.n * unit);
    links.push({ key: `${m.id}-${o.id}`, d: ribbon(m, o, m.y, oOff.get(o.id), h), phase: m.phase });
    oOff.set(o.id, oOff.get(o.id) + h);
  }
  if (!fold.docs.length) return <div className="tv-empty">No sources yet.</div>;
  return (
    <svg className="tv-ribbons" viewBox={`0 0 ${W} ${H}`} style={{ height }} role="img" aria-label="Legs to sources to outcome">
      {links.map((l) => <path key={l.key} d={l.d} className={`tv-ribbon is-${l.phase}`} />)}
      {L.map((l) => <g key={l.id} className="tv-rib-node is-leg"><rect x={l.x} y={l.y} width={l.w} height={l.h} rx={3} /><text x={l.x + l.w + 6} y={l.y + Math.min(l.h / 2 + 4, 14)}>{l.label} · {l.count}</text></g>)}
      {M.map((m) => { const href = labels && hrefFor && m.id !== '__dropped' ? hrefFor(m.id) : null; const text = labels || m.id === '__dropped' ? clip(m.label, 36) : 'source'; const t = <text x={m.x + m.w + 6} y={m.y + Math.min(m.h / 2 + 4, 14)}>{text}</text>; return <g key={m.id} className={`tv-rib-node is-${m.phase}`}><rect x={m.x} y={m.y} width={m.w} height={m.h} rx={3} />{href ? <Link to={href} href={href}>{t}</Link> : t}</g>; })}
      {O.map((o) => <g key={o.id} className={`tv-rib-node is-out is-${o.id}`}><rect x={o.x} y={o.y} width={o.w} height={o.h} rx={3} /><text x={o.x - 6} y={o.y + Math.min(o.h / 2 + 4, 14)} textAnchor="end">{o.label}{o.id !== 'dropped' ? ` · ${o.count}` : ` · ${model.dropped.length}`}</text></g>)}
    </svg>
  );
}

// ---- the heat grid -----------------------------------------------------------------------
// Every page of the map as a cell, grouped by stratum then type; the cell's
// warmth is how often NoVA served it lately (retrievalHeat), and the current
// search lights its cells by phase. Needs a graph and a heat object (key or
// slug → count); without a graph the view is not offered.
export function HeatGrid({ graph, heat, fold, keyOf, onOpen = null, labels = true }) {
  const cells = useMemo(() => {
    const nodes = (graph?.nodes || []).map((n) => ({ ...n, key: n.key || n.slug, stratum: n.stratum || 'brain', type: n.type || n.folder || '' }));
    nodes.sort((a, b) => (a.stratum > b.stratum ? 1 : a.stratum < b.stratum ? -1 : a.type > b.type ? 1 : a.type < b.type ? -1 : String(a.title).localeCompare(String(b.title))));
    const max = Math.max(1, ...nodes.map((n) => Number(heat?.[n.key] || heat?.[n.slug] || 0)));
    return nodes.map((n) => ({ ...n, heat: Math.log1p(Number(heat?.[n.key] || heat?.[n.slug] || 0)) / Math.log1p(max) }));
  }, [graph, heat]);
  const groups = useMemo(() => { const m = new Map(); for (const c of cells) { const k = `${c.stratum}·${c.type}`; const g = m.get(k) || { stratum: c.stratum, type: c.type, cells: [] }; g.cells.push(c); m.set(k, g); } return [...m.values()]; }, [cells]);
  if (!cells.length) return <div className="tv-empty">No map to grid.</div>;
  return (
    <div className="tv-heat" role="img" aria-label={`Heat grid: ${cells.length} pages`}>
      {groups.map((g) => (
        <div key={`${g.stratum}·${g.type}`} className="tv-heat-group">
          <h6>{STRATUM_LABEL[g.stratum] || g.stratum}{g.type ? ` · ${g.type}` : ''} <span>{g.cells.length}</span></h6>
          <div className="tv-heat-cells">
            {g.cells.map((c) => {
              const phase = fold.phases[c.key] || null;
              const hot = fold.pulse?.has(c.key);
              const cell = <i key={c.key} className={`tv-cell ${phase ? `is-${phase}` : ''} ${hot ? 'is-hot' : ''}`} style={{ '--heat': c.heat.toFixed(2) }} title={labels ? `${c.title}${heat?.[c.key] || heat?.[c.slug] ? ` · served ${heat[c.key] || heat[c.slug]}×` : ''}${phase ? ` · ${phase}` : ''}` : undefined} />;
              return onOpen ? <button key={c.key} type="button" className="tv-cell-btn" onClick={() => onOpen(c)} aria-label={labels ? c.title : 'page'}>{cell}</button> : cell;
            })}
          </div>
        </div>
      ))}
      <div className="tv-heat-legend"><i style={{ '--heat': 0 }} /> never <i style={{ '--heat': 0.5 }} /> sometimes <i style={{ '--heat': 1 }} /> often served · <i className="is-selected" /> kept <i className="is-cited" /> cited <i className="is-opened" /> opened</div>
    </div>
  );
}

// ---- the trails --------------------------------------------------------------------------
// The last N searches as columns (oldest left), the pages they served as
// rows (most-served first); a dot where a search served a page, a line
// where the same page carried over to the next search. The current stream
// is the rightmost column, lit by phase — the footprints lead to it.
export function Trails({ traces = [], fold = null, width = 600, maxRows = 24, labels = true, hrefFor = null, LinkComponent = 'a' }) {
  const Link = LinkComponent;
  const model = useMemo(() => {
    const cols = traces.slice().sort((a, b) => (a.at || 0) - (b.at || 0)).map((t) => ({ id: t.id, at: t.at, question: t.question, docs: new Map((t.hits || []).map((h) => [h.docId, h.title || h.docId])) }));
    if (fold && fold.docs.length) cols.push({ id: '__now', at: Date.now(), now: true, question: 'this search', docs: new Map(fold.docs.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).map((d) => [d.docId, d.title])), phases: new Map(fold.docs.map((d) => [d.docId, d.phase])) });
    const count = new Map();
    for (const c of cols) for (const [id, title] of c.docs) { const cur = count.get(id) || { id, title, n: 0 }; cur.n += 1; count.set(id, cur); }
    const rows = [...count.values()].sort((a, b) => b.n - a.n || String(a.title).localeCompare(String(b.title))).slice(0, maxRows);
    return { cols, rows };
  }, [traces, fold, maxRows]);
  const { cols, rows } = model;
  if (!cols.length || !rows.length) return <div className="tv-empty">No recent searches to trail.</div>;
  const labelW = labels ? 190 : 20; const rowH = 18; const top = 26;
  const W = width; const H = top + rows.length * rowH + 8;
  const x = (i) => labelW + 10 + (i / Math.max(1, cols.length - 1)) * (W - labelW - 24);
  const y = (i) => top + i * rowH + rowH / 2;
  return (
    <svg className="tv-trails" viewBox={`0 0 ${W} ${H}`} style={{ height: H }} role="img" aria-label={`Trails: ${cols.length} searches, ${rows.length} pages`}>
      {cols.map((c, i) => <g key={c.id} className={`tv-trail-col ${c.now ? 'is-now' : ''}`}><line x1={x(i)} x2={x(i)} y1={top - 4} y2={H - 6} /><text x={x(i)} y={12} textAnchor="middle"><title>{c.question || ''}</title>{c.now ? 'now' : i === 0 || i === cols.length - 1 || cols.length <= 8 ? new Date(c.at || 0).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''}</text></g>)}
      {rows.map((r, ri) => {
        const href = labels && hrefFor ? hrefFor(r.id) : null;
        const label = <text x={labelW} y={y(ri) + 4} textAnchor="end" className="tv-trail-label">{labels ? clip(r.title, 30) : ''}</text>;
        const pts = cols.map((c, ci) => (c.docs.has(r.id) ? ci : null));
        let d = '';
        for (let i = 1; i < pts.length; i += 1) if (pts[i] != null && pts[i - 1] != null) d += `M ${x(i - 1)} ${y(ri)} L ${x(i)} ${y(ri)} `;
        return (
          <g key={r.id} className="tv-trail-row">
            {href ? <Link to={href} href={href}>{label}</Link> : label}
            <line x1={labelW + 10} x2={W - 14} y1={y(ri)} y2={y(ri)} className="tv-trail-base" />
            {d ? <path d={d} className="tv-trail-link" /> : null}
            {pts.map((ci) => (ci == null ? null : <circle key={ci} cx={x(ci)} cy={y(ri)} r={cols[ci].now ? 5 : 3.5} className={`tv-trail-dot ${cols[ci].now ? `is-now is-${cols[ci].phases?.get(r.id) || 'selected'}` : ''}`} />))}
            <text x={W - 10} y={y(ri) + 4} textAnchor="end" className="tv-trail-n">{r.n}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ---- compare: two streams of one question ----------------------------------------------
// `a` is the recorded search, `b` the re-ask: `{ label, events, reply,
// citations, withheld }`. mode 'test' (b withheld a's cited pages): did the
// answer lose its citations — were they load-bearing? mode 'again' (same
// question, today's corpus): what changed since.
const notCovered = (text) => /does not contain|cannot answer|can't answer|can’t answer|not covered|no information|no relevant|don't have|do not have|not able to find/i.test(String(text || ''));
export function compareVerdict(a, b, mode) {
  const fa = foldThink(a.events || [], (a.events || []).length - 1, () => null);
  const fb = foldThink(b.events || [], (b.events || []).length - 1, () => null);
  const citedA = fa.docs.filter((d) => d.phase === 'cited').map((d) => d.docId);
  const citedB = fb.docs.filter((d) => d.phase === 'cited').map((d) => d.docId);
  const keptA = new Set(fa.docs.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).map((d) => d.docId));
  const keptB = new Set(fb.docs.filter((d) => PHASE_RANK[d.phase] >= PHASE_RANK.selected).map((d) => d.docId));
  const same = [...keptA].filter((d) => keptB.has(d));
  const onlyA = [...keptA].filter((d) => !keptB.has(d));
  const onlyB = [...keptB].filter((d) => !keptA.has(d));
  const titleOf = (id) => (fa.docs.find((d) => d.docId === id) || fb.docs.find((d) => d.docId === id) || { title: id }).title;
  if (mode === 'test') {
    const w = (b.withheld || []).length;
    const declined = notCovered(b.reply);
    if (!citedB.length || declined) return { id: 'load-bearing', tone: 'ok', label: 'load-bearing', text: `Without the ${w} withheld page${w === 1 ? '' : 's'}, NoVA ${citedB.length ? `cited ${citedB.length} but said the question is not covered` : 'cited nothing'} — the original citations carried the answer.`, same, onlyA, onlyB, titleOf };
    return { id: 'other-support', tone: 'warn', label: 'other support', text: `Without the ${w} withheld page${w === 1 ? '' : 's'}, NoVA still answered, citing ${citedB.length} other page${citedB.length === 1 ? '' : 's'} — the claim has support beyond the original citations. Read both answers: do they agree?`, same, onlyA, onlyB, titleOf };
  }
  const changed = onlyA.length + onlyB.length;
  const sameCite = citedA.length === citedB.length && citedA.every((d) => citedB.includes(d));
  return { id: changed ? 'drifted' : 'stable', tone: changed ? 'info' : 'ok', label: changed ? `${changed} changed` : 'stable', text: changed ? `Same question today: ${same.length} source${same.length === 1 ? '' : 's'} in common, ${onlyB.length} new, ${onlyA.length} no longer served${sameCite ? '; the citations are the same' : '; the citations moved'}.` : `Same question today: the same ${same.length} source${same.length === 1 ? '' : 's'}${sameCite ? ' and the same citations' : ', the citations moved'} — the brain has not changed its mind.`, same, onlyA, onlyB, titleOf };
}
export function Compare({ a, b, mode = 'test', labels = true, hrefFor = null, LinkComponent = 'a', height = 240 }) {
  const Link = LinkComponent;
  const v = useMemo(() => compareVerdict(a, b, mode), [a, b, mode]);
  const side = (s) => {
    const ev = s.events || [];
    const fold = foldThink(ev, ev.length - 1, () => null);
    return (
      <div className="tv-cmp-side">
        <h6>{s.label}{s.withheld?.length ? <span> · {s.withheld.length} withheld</span> : null}</h6>
        <Sonar fold={fold} events={ev} cursor={ev.length - 1} width={300} height={height} labels={labels} hrefFor={hrefFor} LinkComponent={LinkComponent} />
        <Scorecard fold={fold} events={ev} cursor={ev.length - 1} compact />
        {s.reply ? <p className="tv-cmp-reply">{clip(s.reply, 260)}</p> : null}
      </div>
    );
  };
  const list = (ids, cls) => (ids.length ? <ul className={`tv-cmp-list ${cls}`}>{ids.slice(0, 8).map((id) => { const href = labels && hrefFor ? hrefFor(id) : null; const t = labels ? clip(v.titleOf(id), 40) : 'a source'; return <li key={id}>{href ? <Link to={href} href={href}>{t}</Link> : t}</li>; })}{ids.length > 8 ? <li>+{ids.length - 8}</li> : null}</ul> : null);
  return (
    <div className={`tv-cmp is-${v.tone}`} role="group" aria-label="Compare two searches">
      <div className="tv-cmp-sides">{side(a)}{side(b)}</div>
      <div className="tv-cmp-verdict"><b>{v.label}</b><p>{v.text}</p></div>
      <div className="tv-cmp-diff">
        <div><h6>in both <span>{v.same.length}</span></h6>{list(v.same, 'is-same')}</div>
        <div><h6>only {a.label} <span>{v.onlyA.length}</span></h6>{list(v.onlyA, 'is-gone')}</div>
        <div><h6>only {b.label} <span>{v.onlyB.length}</span></h6>{list(v.onlyB, 'is-new')}</div>
      </div>
    </div>
  );
}
